import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import { type MouseEvent, useState, useEffect, useRef } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import computeAllSlotTimings, { calculateFlowDurationFromSlots } from '../slot-list/calculate';
import useUpdateSessionMutation from '@/state/mutations/sessions/use-update-session';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowMode } from '../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';

export default function SlotActions({
  slotId,
  slotTitle,
  onSuccess,
  externalTrigger,
  hideButton,
  onSlotDeleted,
}: {
  slotId: string;
  slotTitle: string;
  flowId: string;
  onSuccess?: () => void;
  externalTrigger?: boolean;
  hideButton?: boolean;
  onSlotDeleted?: () => void;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);
  const [deletePending, setDeletePending] = useState(false);
  const [isDeletingSessionNotInLibrary, setIsDeletingSessionNotInLibrary] = useState(false);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const flow = useFlowStore((s) => s.flow);
  const sessions = useEventStore((s) => s.eventSessions);
  const { mutateAsync: updateSessionMutation, isPending: isUpdatingSession } = useUpdateSessionMutation();
  const mode = useFlowMode();

  const openDeleteDialog = () => {
    const slot = flow?.slots?.find((slot) => slot.id === slotId);
    if (slot?.contentType === 'Session' && slot?.content) {
      const session = slot.content;
      if (session && !session.isInLibrary) {
        setIsDeletingSessionNotInLibrary(true);
      } else {
        setIsDeletingSessionNotInLibrary(false);
      }
    } else {
      setIsDeletingSessionNotInLibrary(false);
    }

    setIsDeleteDialogOpen(true);
  };

  const handleDeleteIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openDeleteDialog();
  };

  // Handle external trigger (e.g., from swipe-to-delete)
  const prevExternalTrigger = useRef(false);
  useEffect(() => {
    if (externalTrigger && !prevExternalTrigger.current && flow) {
      openDeleteDialog();
    }
    prevExternalTrigger.current = externalTrigger ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTrigger, flow, slotId]);

  const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteSession = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!flow) return;

    setDeletePending(true);
    const filteredSlots = flow.slots?.filter((slot) => slot.id !== slotId);
    const durationToUse = flow.slot?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(filteredSlots ?? []);
    const computedSlots = computeAllSlotTimings(filteredSlots ?? [], durationToUse);
    const updatedFlow = {
      ...flow,
      slots: computedSlots,
    };

    await updateSession();
    if (!canEmit) {
      notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
      return;
    }
    const { ok } = await emitFlowSync({ flow: updatedFlow as Record<string, unknown> });
    if (ok) {
      notification.notifySuccess(
        isDeletingSessionNotInLibrary
          ? t('flows.detail.slotList.detail.deleteSlot.notifications.deleteSuccess', { title: slotTitle })
          : t('flows.detail.slotList.detail.deleteSlot.notifications.removeSessionSuccess', { title: slotTitle }),
      );
    } else {
      notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
    }
    setIsDeleteDialogOpen(false);
    setSelectedSlot(undefined);
    onSlotDeleted?.();
    onSuccess?.();
  };

  const updateSession = async () => {
    const slot = flow?.slots?.find((slot) => slot.id === slotId);
    if (!slot?.content) return;
    const session = sessions?.find((s) => s.id === slot?.content?.id);
    if (!session) return;
    const isLastUsageOfContent = flow?.slots?.filter((s) => s.content?.id === session.id).length === 1;
    if (!isLastUsageOfContent) return;

    const updateSessionDto: UpdateSessionDto = {
      id: session.id,
      version: session.version,
      usedInEntities: session.usedInEntities
        ?.filter((e) => e.entity.id !== flow?.id)
        .map((e) => ({
          entity: e.entity.id,
          entityType: e.entityType,
        })),
    };

    try {
      await updateSessionMutation(updateSessionDto);
    } catch {
      notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
    }
  };

  // Determine if session is in library to show appropriate icon
  const slot = flow?.slots?.find((slot) => slot.id === slotId);
  const session = slot?.contentType === 'Session' && slot?.content ? slot.content : null;
  const isSessionInLibrary = session?.isInLibrary ?? false;

  return (
    <>
      {!hideButton && (
        <Tooltip
          title={
            isSessionInLibrary
              ? t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
              : t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
          }
        >
          <IconButton
            aria-label={isSessionInLibrary ? 'remove' : 'delete'}
            onClick={(e) => handleDeleteIconClick(e)}
            loading={deletePending || isUpdatingSession}
            disabled={isOrganizeMode(mode)}
            size="small"
          >
            {isSessionInLibrary ? <RemoveCircleOutline fontSize="small" /> : <DeleteIcon />}
          </IconButton>
        </Tooltip>
      )}
      <ConfirmationModal
        open={isDeleteDialogOpen}
        isLoading={deletePending}
        title={
          isDeletingSessionNotInLibrary
            ? t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
            : t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
        }
        text={
          isDeletingSessionNotInLibrary
            ? t('flows.detail.slotList.detail.deleteSlot.sessionText', { title: slotTitle })
            : t('flows.detail.slotList.detail.deleteSlot.removeSessionText', { title: slotTitle })
        }
        onConfirm={handleDeleteSession}
        onCancel={handleDeleteModalCancel}
      />
    </>
  );
}
